import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faFile, faFilePdf, faFileLines, faDownload, faTrash, faPen, faCheck, faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { SiteContext } from '../../Contexts';
import { FileItem, OrderDirection } from '../../Models/Models';
import { FieldLengths } from '../../Models/FieldLengths';
import CharCount from '../Form/CharCount';
import { FileService } from '../../Services/FileService';
import { EntityColors } from '../../Themes/EntityColors';
import { Utils } from '../../Services/Utils';
import TableShell from '../Table/TableShell';
import TableToolbar from '../Table/TableToolbar';
import FileDeleteModal from '../Modals/FileDeleteModal';
import UserAvatarCell from '../Avatar/UserAvatarCell';

const FileTableComponent: React.FC = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Súbory');
    }, []);

    const { filterFiles, deleteFile, editFileName } = FileService();
    const { currentSite }: any = useContext(SiteContext);
    const { action, icon } = EntityColors.file;

    const [files, setFiles] = useState<FileItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [orderDirection, setOrderDirection] = useState<OrderDirection>('Desc');
    const [loading, setLoading] = useState(true);
    const [showDelete, setShowDelete] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<FileItem | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const load = async (pageNum: number, opts?: { perPage?: number; order?: OrderDirection }) => {
        if (currentSite.id === -1) return;
        setLoading(true);
        const limit = opts?.perPage ?? perPage;
        const data = await filterFiles({
            websiteId: currentSite.id,
            limit,
            offset: (pageNum - 1) * limit,
            orderDirection: opts?.order ?? orderDirection,
        });
        if (data) {
            setFiles(data.files ?? []);
            setTotalCount(data.totalCount ?? 0);
        }
        setLoading(false);
    };

    useEffect(() => { void load(1); setPage(1); }, [currentSite, refreshTrigger]);

    const handlePageChange = (p: number) => {
        setPage(p);
        void load(p);
    };

    const changePerPage = (count: number) => {
        setPerPage(count);
        setPage(1);
        void load(1, { perPage: count });
    };

    const changeOrder = (dir: OrderDirection) => {
        setOrderDirection(dir);
        setPage(1);
        void load(1, { order: dir });
    };

    const startDelete = (file: FileItem) => {
        setFileToDelete(file);
        setShowDelete(true);
    };

    const confirmDelete = async () => {
        if (!fileToDelete) return;
        await deleteFile(fileToDelete.id)
            .then(() => { setShowDelete(false); setFileToDelete(null); setRefreshTrigger(r => r + 1); })
            .catch(() => { setShowDelete(false); });
    };

    const handleRename = async (file: FileItem, newFileName: string) => {
        await editFileName(file.id, newFileName)
            .then(() => setRefreshTrigger(r => r + 1))
            .catch(() => {});
    };

    return (
        <>
            <TableShell
                title="Súbory"
                icon={icon}
                accentColor={action}
                createHref="/file/add"
                createLabel="Nahrať"
                isLoading={loading}
                isEmpty={!loading && files.length === 0}
                emptyMessage="Žiadne súbory"
                page={page}
                totalCount={totalCount}
                perPage={perPage}
                onPageChange={handlePageChange}
                toolbar={
                    <TableToolbar
                        totalCount={totalCount}
                        nounForms={['súbor', 'súbory', 'súborov']}
                        perPage={perPage}
                        onPerPageChange={changePerPage}
                        pageSizes={[15, 30, 60]}
                        orderDirection={orderDirection}
                        onOrderChange={changeOrder}
                    />
                }
            >
                <table style={{ '--file-accent': action } as React.CSSProperties}>
                    <thead>
                        <tr>
                            <th>Súbor</th>
                            <th>Typ</th>
                            <th>Vytvorené</th>
                            <th>Vytvoril</th>
                            <th>Naposledy upravené</th>
                            <th>Upravil</th>
                            <th className="w-1"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {files.map(file => (
                            <FileRow
                                key={file.id}
                                file={file}
                                onDelete={() => startDelete(file)}
                                onRename={newName => handleRename(file, newName)}
                            />
                        ))}
                    </tbody>
                </table>
            </TableShell>

            <FileDeleteModal
                show={showDelete}
                onHide={() => { setShowDelete(false); setFileToDelete(null); }}
                onSubmit={confirmDelete}
                idToDelete={fileToDelete?.id ?? 0}
                fileName={fileToDelete?.fileName}
            />
        </>
    );
};

const FileRow: React.FC<{
    file: FileItem;
    onDelete: () => void;
    onRename: (newFileName: string) => Promise<void>;
}> = ({ file, onDelete, onRename }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    const dotIndex = file.fileName.lastIndexOf('.');
    const baseName = dotIndex > 0 ? file.fileName.slice(0, dotIndex) : file.fileName;
    // Extension (including the dot) is fixed - renaming can never change the file's type.
    const extension = dotIndex > 0 ? file.fileName.slice(dotIndex) : '';
    const ext = extension.slice(1).toLowerCase();

    const [editValue, setEditValue] = useState(baseName);

    const { fileIcon, iconColor } = getFileStyle(ext);

    const startEditing = () => {
        setEditValue(baseName);
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setEditValue(baseName);
    };

    const saveEditing = async () => {
        const trimmed = editValue.trim();
        if (!trimmed || trimmed === baseName) {
            setIsEditing(false);
            return;
        }
        setSaving(true);
        await onRename(trimmed + extension);
        setSaving(false);
        setIsEditing(false);
    };

    return (
        <tr style={{ '--file-color': iconColor } as React.CSSProperties}>
            <td>
                <div className="st-file-cell">
                    <div className="st-file-icon-badge st-file-icon-badge--tint">
                        <FontAwesomeIcon icon={fileIcon} />
                    </div>
                    {isEditing ? (
                        <div className="st-file-rename">
                            <input
                                type="text"
                                className="st-file-rename__input"
                                value={editValue}
                                autoFocus
                                disabled={saving}
                                // The extension is kept, so it counts towards the file name limit too.
                                maxLength={FieldLengths.fileName - extension.length}
                                onChange={e => setEditValue(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') void saveEditing();
                                    if (e.key === 'Escape') cancelEditing();
                                }}
                            />
                            {extension && (
                                <span className="st-file-rename__ext" title="Typ súboru sa pri premenovaní nedá zmeniť">
                                    {extension}
                                </span>
                            )}
                            <CharCount value={editValue + extension} max={FieldLengths.fileName} />
                        </div>
                    ) : (
                        <span className="st-file-row-name">{file.fileName}</span>
                    )}
                </div>
            </td>
            <td>
                <span className="st-file-type-pill">{ext || '—'}</span>
            </td>
            <td className="text-nowrap">
                {file.createdAt ? Utils.formatDate(file.createdAt) : '—'}
            </td>
            <td>
                <UserAvatarCell userId={file.createdById} />
            </td>
            <td className="text-nowrap st-cell-soft">
                {file.modifiedAt ? Utils.formatDate(file.modifiedAt) : '—'}
            </td>
            <td>
                <UserAvatarCell userId={file.modifiedById} />
            </td>
            <td>
                <div className="st-file-actions">
                    {isEditing ? (
                        <>
                            <RowIconButton icon={faCheck} title="Uložiť" tone="success" disabled={saving} onClick={() => void saveEditing()} />
                            <RowIconButton icon={faXmark} title="Zrušiť" tone="danger" disabled={saving} onClick={cancelEditing} />
                        </>
                    ) : (
                        <>
                            <a
                                href={file.filePath}
                                target="_blank"
                                rel="noreferrer"
                                title="Stiahnuť"
                                aria-label="Stiahnuť"
                                className="st-row-icon-btn st-row-icon-btn--accent"
                            >
                                <FontAwesomeIcon icon={faDownload} />
                            </a>
                            <RowIconButton icon={faPen} title="Premenovať" tone="accent" onClick={startEditing} />
                            <RowIconButton icon={faTrash} title="Zmazať" tone="danger" onClick={onDelete} />
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
};

/** Square icon button for table rows; `tone` picks its hover colour. */
const RowIconButton: React.FC<{
    icon: IconDefinition;
    title: string;
    tone: 'accent' | 'success' | 'danger';
    onClick: () => void;
    disabled?: boolean;
}> = ({ icon, title, tone, onClick, disabled }) => (
    <button
        type="button"
        className={`st-row-icon-btn st-row-icon-btn--${tone}`}
        title={title}
        aria-label={title}
        disabled={disabled}
        onClick={onClick}
    >
        <FontAwesomeIcon icon={icon} />
    </button>
);

function getFileStyle(ext: string): { fileIcon: any; iconColor: string } {
    switch (ext) {
        case 'pdf':  return { fileIcon: faFilePdf,   iconColor: '#EF4444' };
        case 'doc':
        case 'docx': return { fileIcon: faFileLines, iconColor: '#3B82F6' };
        case 'xls':
        case 'xlsx': return { fileIcon: faFileLines, iconColor: '#10B981' };
        case 'ppt':
        case 'pptx': return { fileIcon: faFileLines, iconColor: '#F97316' };
        case 'txt':
        case 'csv':  return { fileIcon: faFileLines, iconColor: '#8B5CF6' };
        default:     return { fileIcon: faFile,      iconColor: '#64748B' };
    }
}

export default FileTableComponent;
